import type {
  TileContentLease,
  TileContentPackage,
  TileResourceCosts,
} from '@/plugins/tiles/contracts'
import type { TileCacheBudget } from '@/plugins/tiles/runtime/TileCacheBudget'
import type { TileContentRegistry } from '@/plugins/tiles/runtime/TileContentRegistry'
import { TilesError } from '@/plugins/tiles/TilesError'

export type TileCachePin = 'display' | 'fallback' | 'candidate' | 'oldSnapshot' | 'collision'
export type TileCacheAdmission =
  | { readonly accepted: true }
  | {
      readonly accepted: false
      readonly reason: 'oversized' | 'protected-budget' | 'closed' | 'conflict'
    }
interface CacheEntry {
  readonly content: TileContentPackage
  bytes: number
  memory: TileMemoryBytes
  transientBytes: number
  readonly pins: Map<TileCachePin, TileContentLease[]>
  used: number
  usedAt: number
}

export interface TileMemoryBytes {
  readonly cpuBytes: number
  readonly gpuBytes: number
}

interface CostEstimate extends TileMemoryBytes {
  readonly bytes: number
}

/** 缓存兼容标量成本包含 CPU/GPU 纹理分账之和。 */
export function tileCostBytes(cost: number | TileResourceCosts): number {
  const values =
    typeof cost === 'number'
      ? [cost]
      : [
          cost.inputBytes,
          cost.geometryBytes,
          cost.textureBytes,
          cost.transientBytes,
          ...(cost.cpuTextureBytes === undefined ? [] : [cost.cpuTextureBytes]),
          ...(cost.gpuTextureBytes === undefined ? [] : [cost.gpuTextureBytes]),
          ...(cost.memory ? [cost.memory.cpuBytes, cost.memory.gpuBytes] : []),
        ]
  if (
    values.some((value) => !Number.isFinite(value) || value < 0) ||
    !Number.isFinite(values.reduce((sum, value) => sum + value, 0))
  ) {
    throw new TilesError({
      code: 'invalid-input',
      stage: 'runtime',
      message: 'Tiles 缓存估算成本必须有限且非负',
    })
  }
  return typeof cost === 'number'
    ? cost
    : cost.memory
      ? cost.memory.cpuBytes + cost.memory.gpuBytes
      : cost.inputBytes +
        cost.geometryBytes +
        Math.max(cost.textureBytes, (cost.cpuTextureBytes ?? 0) + (cost.gpuTextureBytes ?? 0)) +
        cost.transientBytes
}

function estimateMemory(cost: number | TileResourceCosts): CostEstimate {
  const bytes = tileCostBytes(cost)
  if (typeof cost === 'number') return { bytes, cpuBytes: bytes, gpuBytes: bytes }
  return { bytes, ...tileMemoryCosts(cost) }
}

/** 缓存、恢复准入与延迟释放共用同一 CPU/GPU 保守估算。 */
export function tileMemoryCosts(cost: TileResourceCosts): TileMemoryBytes {
  if (cost.memory) return cost.memory
  return {
    cpuBytes: Math.max(
      cost.inputBytes + cost.geometryBytes + (cost.cpuTextureBytes ?? cost.textureBytes),
      cost.transientBytes
    ),
    gpuBytes: cost.geometryBytes + (cost.gpuTextureBytes ?? cost.textureBytes),
  }
}

/** 软件估算预算。缓存仅淘汰无租约内容，保护引用退出前允许明确的超预算状态。 */
export class TileCache {
  private _entries = new Map<string, CacheEntry>()
  private _reservations = new Map<string, CostEstimate>()
  private _clock = 0
  private _closed = false
  private _maxBytes: number
  private readonly _unsubscribe: () => void

  constructor(
    maxCacheBytes: number,
    private readonly _registry: TileContentRegistry,
    private readonly _pool?: TileCacheBudget
  ) {
    this._maxBytes = this._validateBudget(maxCacheBytes)
    this._pool?.register(this)
    this._unsubscribe = this._registry.onChange(() => {
      this.tryUnregister()
      this._pool?.notifyCapacityChanged()
    })
  }

  configure(maxCacheBytes: number): void {
    this._maxBytes = this._validateBudget(maxCacheBytes)
    this.evict()
  }

  /** 同 ID 重估替换原预约；预约费用表示尚未成为驻留包的输入/解码峰值。 */
  reserve(id: string, cost: number | TileResourceCosts): TileCacheAdmission {
    if (this._closed) return { accepted: false, reason: 'closed' }
    const estimate = estimateMemory(cost)
    const { bytes } = estimate
    if (this._entries.has(id)) return { accepted: true }
    if (bytes > this._maxBytes) return { accepted: false, reason: 'oversized' }
    const previous = this._reservations.get(id)
    if (
      !this._makeRoom(bytes - (previous?.bytes ?? 0), id, {
        cpuBytes: estimate.cpuBytes - (previous?.cpuBytes ?? 0),
        gpuBytes: estimate.gpuBytes - (previous?.gpuBytes ?? 0),
      })
    )
      return { accepted: false, reason: 'protected-budget' }
    this._reservations.set(id, estimate)
    return { accepted: true }
  }

  cancelReservation(id: string): void {
    if (this._reservations.delete(id)) this._pool?.notifyCapacityChanged()
    this.tryUnregister()
  }

  /** 取消中的不可中断解码转移预约，避免同 ID 新世代复用旧额度。 */
  transferReservation(from: string, to: string): void {
    if (this._closed || this._reservations.has(to)) return
    const bytes = this._reservations.get(from)
    if (bytes === undefined) return
    this._reservations.delete(from)
    this._reservations.set(to, bytes)
  }

  /** 始终接管输入；失败时取消预约并释放该新包，调用方无需重复 dispose。 */
  admit(id: string, content: TileContentPackage): TileCacheAdmission {
    const reject = (
      reason: 'oversized' | 'protected-budget' | 'closed' | 'conflict'
    ): TileCacheAdmission => {
      this.cancelReservation(id)
      this._registry.discard(content)
      return { accepted: false, reason }
    }
    if (this._closed) return reject('closed')
    const existing = this._entries.get(id)
    if (existing) {
      this.cancelReservation(id)
      if (existing.content !== content) return reject('conflict')
      this.touch(id)
      return { accepted: true }
    }
    let estimate: CostEstimate
    try {
      estimate = estimateMemory(content.costs)
    } catch (error) {
      this.cancelReservation(id)
      this._registry.discard(content)
      throw error
    }
    if (estimate.bytes > this._maxBytes) return reject('oversized')
    const reservation = this._reservations.get(id)
    if (
      !this._makeRoom(estimate.bytes - (reservation?.bytes ?? 0), id, {
        cpuBytes: estimate.cpuBytes - (reservation?.cpuBytes ?? 0),
        gpuBytes: estimate.gpuBytes - (reservation?.gpuBytes ?? 0),
      })
    )
      return reject('protected-budget')
    if (!this._registry.register(id, content)) return reject('conflict')
    this.cancelReservation(id)
    this._entries.set(id, {
      content,
      bytes: estimate.bytes,
      memory: { cpuBytes: estimate.cpuBytes, gpuBytes: estimate.gpuBytes },
      transientBytes: content.costs.transientBytes,
      pins: new Map(),
      used: this._nextUse(),
      usedAt: Date.now(),
    })
    return { accepted: true }
  }

  get(id: string): TileContentPackage | undefined {
    this.touch(id)
    return this._entries.get(id)?.content
  }

  peek(id: string): TileContentPackage | undefined {
    return this._entries.get(id)?.content
  }

  /** 调用方已持有旧包租约和新图片峰值预约；同步提交后转为新驻留与旧 retirement。 */
  commitCosts(
    id: string,
    reservationId: string,
    incoming: TileResourceCosts,
    apply: () => void
  ): boolean {
    const entry = this._entries.get(id)
    const reservation = this._reservations.get(reservationId)
    if (!entry || !reservation || this._closed) return false
    const added = estimateMemory(incoming)
    if (
      !this._makeRoom(added.bytes - reservation.bytes, id, {
        cpuBytes: added.cpuBytes - reservation.cpuBytes,
        gpuBytes: added.gpuBytes - reservation.gpuBytes,
      })
    )
      return false
    apply()
    const next = estimateMemory(entry.content.costs)
    entry.bytes = next.bytes
    entry.memory = { cpuBytes: next.cpuBytes, gpuBytes: next.gpuBytes }
    entry.transientBytes = entry.content.costs.transientBytes
    this.cancelReservation(reservationId)
    return true
  }

  getEntryBytes(id: string): number | undefined {
    return this._entries.get(id)?.bytes
  }

  entries(): ReadonlyArray<{
    id: string
    bytes: number
    cpuBytes: number
    gpuBytes: number
    used: number
    usedAt: number
    protected: boolean
  }> {
    return [...this._entries].map(([id, entry]) => ({
      id,
      bytes: entry.bytes,
      cpuBytes: entry.memory.cpuBytes,
      gpuBytes: entry.memory.gpuBytes,
      used: entry.used,
      usedAt: entry.usedAt,
      protected: this._registry.refCount(id) > 0,
    }))
  }

  touch(id: string): void {
    const entry = this._entries.get(id)
    if (entry) {
      entry.used = this._nextUse()
      entry.usedAt = Date.now()
    }
  }

  /** 每次 pin 独立持有一个租约，须对应一次 unpin。 */
  pin(id: string, reason: TileCachePin): boolean {
    const entry = this._entries.get(id)
    if (!entry || this._closed) return false
    const lease = this._registry.retain(id)
    if (!lease) return false
    const pins = entry.pins.get(reason) ?? []
    pins.push(lease)
    entry.pins.set(reason, pins)
    this.touch(id)
    return true
  }

  unpin(id: string, reason: TileCachePin): void {
    const entry = this._entries.get(id)
    const pins = entry?.pins.get(reason)
    pins?.pop()?.release()
    if (!pins?.length) entry?.pins.delete(reason)
  }

  /** 释放预算之外的 LRU；有缓存外租约或任何保护 pin 的内容不能淘汰。 */
  evict(): string[] {
    const previousIds = [...this._entries.keys()]
    this._makeRoom(0)
    return previousIds.filter((id) => !this._entries.has(id))
  }

  getStats(): {
    readonly size: number
    readonly residentBytes: number
    readonly reservedBytes: number
    readonly transientBytes: number
    readonly totalBytes: number
    readonly protectedBytes: number
    readonly budgetExceeded: boolean
  } {
    let residentBytes = 0
    let transientBytes = 0
    let protectedBytes = 0
    for (const [id, entry] of this._entries) {
      residentBytes += entry.bytes - entry.transientBytes
      transientBytes += entry.transientBytes
      if (this._registry.refCount(id)) protectedBytes += entry.bytes
    }
    const reservedBytes =
      [...this._reservations.values()].reduce((sum, value) => sum + value.bytes, 0) +
      this._registry.getDeferredCosts().bytes
    const totalBytes = residentBytes + transientBytes + reservedBytes
    return {
      size: this._entries.size,
      residentBytes,
      reservedBytes,
      transientBytes,
      totalBytes,
      protectedBytes,
      budgetExceeded: totalBytes > this._maxBytes,
    }
  }

  getMemoryStats(): {
    readonly committed: TileMemoryBytes
    readonly protected: TileMemoryBytes
    readonly reserved: TileMemoryBytes
  } {
    let cpuBytes = 0
    let gpuBytes = 0
    let protectedCpuBytes = 0
    let protectedGpuBytes = 0
    for (const [id, entry] of this._entries) {
      cpuBytes += entry.memory.cpuBytes
      gpuBytes += entry.memory.gpuBytes
      if (this._registry.refCount(id)) {
        protectedCpuBytes += entry.memory.cpuBytes
        protectedGpuBytes += entry.memory.gpuBytes
      }
    }
    const reserved = [...this._reservations.values()].reduce<TileMemoryBytes>(
      (sum, value) => ({
        cpuBytes: sum.cpuBytes + value.cpuBytes,
        gpuBytes: sum.gpuBytes + value.gpuBytes,
      }),
      this._registry.getDeferredCosts()
    )
    return {
      committed: { cpuBytes: cpuBytes + reserved.cpuBytes, gpuBytes: gpuBytes + reserved.gpuBytes },
      protected: { cpuBytes: protectedCpuBytes, gpuBytes: protectedGpuBytes },
      reserved,
    }
  }

  dispose(): void {
    if (this._closed) return
    this._closed = true
    if (!this._registry.retirement) this._reservations.clear()
    for (const [id, entry] of this._entries) {
      for (const pins of entry.pins.values()) for (const lease of pins) lease.release()
      this._registry.requestDispose(id)
    }
    this._entries.clear()
    this.tryUnregister()
  }

  /** 只淘汰无租约的最老内容，返回实际释放字节数；预约和保护项不受影响。 */
  releaseUnprotected(id: string): void {
    if (this._registry.refCount(id) !== 0 || !this._entries.has(id)) return
    this._entries.delete(id)
    this._registry.requestDispose(id)
  }

  /** 只淘汰无租约的最老内容，返回实际释放字节数；预约和保护项不受影响。 */
  evictUnprotected(targetFreeBytes: number, excludeId?: string): number {
    let freed = 0
    const before = this.getStats().totalBytes
    const candidates = [...this._entries]
      .filter(([id]) => id !== excludeId && this._registry.refCount(id) === 0)
      .sort((a, b) => a[1].used - b[1].used)
    for (const [id, entry] of candidates) {
      if (freed >= targetFreeBytes) break
      // dispose 回调可能改变其他条目的租约，执行前重新确认。
      if (this._entries.get(id) !== entry || this._registry.refCount(id) !== 0) continue
      this._entries.delete(id)
      this._registry.requestDispose(id)
      freed += entry.bytes
    }
    return Math.max(0, before - this.getStats().totalBytes)
  }

  private _nextUse(): number {
    return this._pool?.nextUse() ?? ++this._clock
  }

  private _makeRoom(
    additional: number,
    exclude?: string,
    additionalMemory: TileMemoryBytes = { cpuBytes: 0, gpuBytes: 0 }
  ): boolean {
    let total = this.getStats().totalBytes + additional
    // 准入先检查本地和共享池是否可满足；预算下调的主动清理仍尽力释放。
    if (exclude !== undefined) {
      if (total > this._maxBytes) {
        const evictable = this.entries().reduce(
          (sum, entry) => sum + (entry.id !== exclude && !entry.protected ? entry.bytes : 0),
          0
        )
        if (total - evictable > this._maxBytes) return false
      }
      if (this._pool && !this._pool.canEnsureCapacity(additional, this, exclude, additionalMemory))
        return false
    }
    if (total <= this._maxBytes)
      return this._pool?.ensureCapacity(additional, this, exclude, additionalMemory) ?? true
    const candidates = [...this._entries]
      .filter(([id]) => id !== exclude && this._registry.refCount(id) === 0)
      .sort((a, b) => a[1].used - b[1].used)
    let scheduled = 0
    for (const [id, entry] of candidates) {
      if (this._entries.get(id) !== entry || this._registry.refCount(id) !== 0) continue
      this._entries.delete(id)
      this._registry.requestDispose(id)
      if (this._registry.retirement) scheduled += entry.bytes
      total = this.getStats().totalBytes + additional
      if (total - scheduled <= this._maxBytes) break
    }
    if (total > this._maxBytes) return false
    return this._pool?.ensureCapacity(additional, this, exclude, additionalMemory) ?? true
  }

  private _validateBudget(bytes: number): number {
    if (!Number.isFinite(bytes) || bytes <= 0)
      throw new TilesError({
        code: 'invalid-input',
        stage: 'runtime',
        message: 'Tiles 缓存预算必须有限且大于零',
      })
    return bytes
  }

  private tryUnregister(): void {
    if (
      !this._closed ||
      this._reservations.size ||
      this._registry.size ||
      this._registry.getDeferredCosts().count
    )
      return
    this._pool?.unregister(this)
    this._unsubscribe?.()
  }
}
